import React, { createContext, useState, useContext, useMemo, useCallback } from 'react';
import { useHistorySettings } from '../appcomponents/events/hooks/useHistorySettings';
import { USER_SETTINGS_DEFAULTS } from '../config/appConfig';

/**
 * Settings Context - Centralized settings management for the entire application
 * Combines History settings and Camera/Face Match settings
 */
const SettingsContext = createContext();

export function SettingsProvider({ children }) {
  // History settings (from existing hook)
  const {
    deleteConfirmation,
    deleteConfirmationRef,
    updateDeleteConfirmation,
    shouldConfirmDelete,
    autoEnableVoice,
    updateAutoEnableVoice,
    stealthMode,
    updateStealthMode
  } = useHistorySettings();

  // Camera/Face Match settings - using defaults from config
  const [removeBg, setRemoveBg] = useState(USER_SETTINGS_DEFAULTS.CAMERA.REMOVE_BG);
  const [sliderValue, setSliderValue] = useState(USER_SETTINGS_DEFAULTS.CAMERA.SLIDER_VALUE);
  const [maxFacesValue, setMaxFacesValue] = useState(USER_SETTINGS_DEFAULTS.CAMERA.MAX_FACES);
  const [showBoundingBoxes, setShowBoundingBoxes] = useState(USER_SETTINGS_DEFAULTS.CAMERA.SHOW_BOUNDING_BOXES);
  const [immediateFaceMatch, setImmediateFaceMatch] = useState(USER_SETTINGS_DEFAULTS.CAMERA.IMMEDIATE_FACE_MATCH);
  const [updateCharacteristicsOnMatch, setUpdateCharacteristicsOnMatch] = useState(USER_SETTINGS_DEFAULTS.FACE_MATCH.UPDATE_CHARACTERISTICS_ON_MATCH);
  const [compressionQuality, setCompressionQuality] = useState(USER_SETTINGS_DEFAULTS.CAMERA.COMPRESSION_QUALITY);
  const [autoAdjustCompression, setAutoAdjustCompression] = useState(USER_SETTINGS_DEFAULTS.CAMERA.AUTO_ADJUST_COMPRESSION);
  const [autoCreateEventOnAIMatch, setAutoCreateEventOnAIMatch] = useState(USER_SETTINGS_DEFAULTS.FACE_MATCH.AUTO_CREATE_EVENT_ON_AI_MATCH);

  // Location settings - using defaults from config
  const [locationHighAccuracy, setLocationHighAccuracy] = useState(USER_SETTINGS_DEFAULTS.LOCATION.HIGH_ACCURACY);
  const [locationAcquisitionTimeout, setLocationAcquisitionTimeout] = useState(USER_SETTINGS_DEFAULTS.LOCATION.ACQUISITION_TIMEOUT);
  const [locationCacheMaxAge, setLocationCacheMaxAge] = useState(USER_SETTINGS_DEFAULTS.LOCATION.CACHE_MAX_AGE);

  // Aggregator settings - using defaults from config
  const [aggregatorEventLimit, setAggregatorEventLimit] = useState(USER_SETTINGS_DEFAULTS.AGGREGATOR.EVENT_LIMIT);

  // Video recording settings - using defaults from config
  const [videoEnabled, setVideoEnabled] = useState(USER_SETTINGS_DEFAULTS.VIDEO.ENABLED);
  const [videoFrameInterval, setVideoFrameInterval] = useState(USER_SETTINGS_DEFAULTS.VIDEO.FRAME_INTERVAL);
  const [videoMaxDuration, setVideoMaxDuration] = useState(USER_SETTINGS_DEFAULTS.VIDEO.MAX_DURATION);
  const [videoSaveToS3, setVideoSaveToS3] = useState(USER_SETTINGS_DEFAULTS.VIDEO.SAVE_TO_S3);
  const [videoAutoStopOnMatch, setVideoAutoStopOnMatch] = useState(USER_SETTINGS_DEFAULTS.VIDEO.AUTO_STOP_ON_MATCH);
  const [videoShowBoundingBoxes, setVideoShowBoundingBoxes] = useState(USER_SETTINGS_DEFAULTS.VIDEO.SHOW_BOUNDING_BOXES);

  // Settings dialog state
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);

  // Memoize callbacks to prevent re-renders
  const openSettings = useCallback(() => setSettingsDialogOpen(true), []);
  const closeSettings = useCallback(() => setSettingsDialogOpen(false), []);

  // Memoize value to prevent unnecessary re-renders
  const value = useMemo(() => ({
    // History settings
    deleteConfirmation,
    deleteConfirmationRef,
    updateDeleteConfirmation,
    shouldConfirmDelete,
    autoEnableVoice,
    updateAutoEnableVoice,
    setAutoEnableVoice: updateAutoEnableVoice, // Alias for backward compatibility
    stealthMode,
    updateStealthMode,
    setStealthMode: updateStealthMode, // Alias for backward compatibility

    // Camera settings
    removeBg,
    setRemoveBg,
    sliderValue,
    setSliderValue,
    maxFacesValue,
    setMaxFacesValue,
    showBoundingBoxes,
    setShowBoundingBoxes,
    immediateFaceMatch,
    setImmediateFaceMatch,
    updateCharacteristicsOnMatch,
    setUpdateCharacteristicsOnMatch,
    compressionQuality,
    setCompressionQuality,
    autoAdjustCompression,
    setAutoAdjustCompression,
    autoCreateEventOnAIMatch,
    setAutoCreateEventOnAIMatch,

    // Location settings
    locationHighAccuracy,
    setLocationHighAccuracy,
    locationAcquisitionTimeout,
    setLocationAcquisitionTimeout,
    locationCacheMaxAge,
    setLocationCacheMaxAge,

    // Aggregator settings
    aggregatorEventLimit,
    setAggregatorEventLimit,

    // Video recording settings
    videoEnabled,
    setVideoEnabled,
    videoFrameInterval,
    setVideoFrameInterval,
    videoMaxDuration,
    setVideoMaxDuration,
    videoSaveToS3,
    setVideoSaveToS3,
    videoAutoStopOnMatch,
    setVideoAutoStopOnMatch,
    videoShowBoundingBoxes,
    setVideoShowBoundingBoxes,

    // Dialog state
    settingsDialogOpen,
    setSettingsDialogOpen,
    openSettings,
    closeSettings
  }), [
    deleteConfirmation,
    deleteConfirmationRef,
    updateDeleteConfirmation,
    shouldConfirmDelete,
    autoEnableVoice,
    updateAutoEnableVoice,
    stealthMode,
    updateStealthMode,
    removeBg,
    sliderValue,
    maxFacesValue,
    showBoundingBoxes,
    immediateFaceMatch,
    updateCharacteristicsOnMatch,
    compressionQuality,
    autoAdjustCompression,
    autoCreateEventOnAIMatch,
    locationHighAccuracy,
    locationAcquisitionTimeout,
    locationCacheMaxAge,
    aggregatorEventLimit,
    videoEnabled,
    videoFrameInterval,
    videoMaxDuration,
    videoSaveToS3,
    videoAutoStopOnMatch,
    videoShowBoundingBoxes,
    settingsDialogOpen,
    openSettings,
    closeSettings
  ]);

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
}
